import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { PresenceActivity } from '@atlaskit/editor-common/collab';

import {
	ExperienceFailure,
	TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
} from '@confluence/experience-tracker';
import { fg } from '@confluence/feature-gating';

import type { ContentMode } from '../presenceTypes';
import { useTotalCount, useTransformedParticipants } from '../presence-store';
import { usePresenceProvider } from '../presence/usePresenceProvider';
import { PresenceAvatarGroup } from '../presence-avatar-group';
import { PresenceChangeboarding } from '../changeboarding/PresenceChangeboarding';

import { usePresence } from './usePresence';

const i18n = defineMessages({
	anonymousUser: {
		id: 'team-presence.avatar-group.anonymous-user',
		defaultMessage: 'Anonymous Person',
		description:
			'The default user name provided for anonymous users when displayed in the avatar group within the real-time presence experience',
	},
});

export type PresenceProps = {
	contentId: string;
	contentMode: ContentMode;
	contentType: string;
	activity: PresenceActivity;
	analyticSource?: string;
};

const PagePresence: FC<PresenceProps> = ({
	contentId,
	contentMode,
	contentType,
	activity,
	analyticSource,
}) => {
	const { formatMessage } = useIntl();

	const { sessionId, fetchMore, networkError, totalParticipants } = usePresence(
		contentId,
		contentType,
		contentMode,
		activity,
	);

	const participants = useTransformedParticipants({
		activity,
		sessionId,
		anonymousName: formatMessage(i18n.anonymousUser),
	});

	if (networkError) {
		return (
			<ExperienceFailure name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} error={networkError} />
		);
	}

	return (
		<PresenceChangeboarding
			contentId={contentId}
			contentMode={contentMode}
			contentType={contentType}
			totalParticipants={totalParticipants}
		>
			<PresenceAvatarGroup
				contentId={contentId}
				contentMode={contentMode}
				contentType={contentType}
				analyticSource={analyticSource}
				onLoadMoreClick={fetchMore}
				participants={participants}
				totalParticipants={totalParticipants}
			/>
		</PresenceChangeboarding>
	);
};

const OldPresence: FC<PresenceProps> = ({
	contentId,
	contentMode,
	contentType,
	activity,
	analyticSource,
}) => {
	const { formatMessage } = useIntl();

	const { sessionId, fetchMore, networkError } = usePresenceProvider(
		contentId,
		contentType,
		contentMode,
		activity,
	);

	const participants = useTransformedParticipants({
		activity,
		sessionId,
		anonymousName: formatMessage(i18n.anonymousUser),
	});

	const totalCount = useTotalCount();

	if (networkError) {
		return (
			<ExperienceFailure name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} error={networkError} />
		);
	}

	return (
		<PresenceChangeboarding
			contentId={contentId}
			contentMode={contentMode}
			contentType={contentType}
			totalParticipants={totalCount}
		>
			<PresenceAvatarGroup
				contentId={contentId}
				contentMode={contentMode}
				contentType={contentType}
				analyticSource={analyticSource}
				onLoadMoreClick={fetchMore}
				participants={participants}
				totalParticipants={totalCount}
			/>
		</PresenceChangeboarding>
	);
};

export const Presence: FC<PresenceProps> = (props) => {
	return fg('confluence_team_presence_batch_refactor') ? (
		<PagePresence {...props} />
	) : (
		<OldPresence {...props} />
	);
};
